import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import apiService from '../services/api';
import { formatNumber, formatNumberForChart } from '../utils/formatNumber';
import { AutomatedBadge, AutoValueBadge, isAutomatedMetric } from './AutomationBadge';

const MetricsDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [metric, setMetric] = useState(null);
  const [metricValues, setMetricValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 25,
    total: 0
  });
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [chartViewType, setChartViewType] = useState('last10'); // 'last10', 'ytd', 'monthly', 'quarterly'
  const [ytdValues, setYtdValues] = useState([]);
  const [ytdLoading, setYtdLoading] = useState(false);
  const [aggregatedValues, setAggregatedValues] = useState([]);
  const [aggregatedLoading, setAggregatedLoading] = useState(false);
  const [formData, setFormData] = useState({
    metrics_id: id || '',
    value_date: new Date().toISOString().split('T')[0], // Set today's date as default
    value: '',
    target: '',
    comment: '',
    created_by: 1
  });

  const fetchMetricDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch metric details
      const metricResponse = await apiService.metrics.getById(id);
      setMetric(metricResponse.data);

      // Fetch metric values with pagination
      const valuesResponse = await apiService.metricValues.getAll({
        metric_id: id,
        page: pagination.page,
        page_size: pagination.page_size,
        sort_by: 'value_date',
        sort_dir: 'desc'
      });

      setMetricValues(valuesResponse.data.items);
      setPagination(prev => ({
        ...prev,
        total: valuesResponse.data.total
      }));

    } catch (err) {
      setError('Failed to load metric details');
      console.error('Error fetching metric details:', err);
    } finally {
      setLoading(false);
    }
  }, [id, pagination.page, pagination.page_size]);

  useEffect(() => {
    fetchMetricDetails();
  }, [fetchMetricDetails]);

  // Fetch YTD data when YTD view is selected
  const fetchYtdData = useCallback(async () => {
    if (!id || chartViewType !== 'ytd') return;
    
    try {
      setYtdLoading(true);
      const currentYear = new Date().getFullYear();
      const yearStart = `${currentYear}-01-01`;
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch all values from beginning of year (no pagination for YTD)
      const valuesResponse = await apiService.metricValues.getAll({
        metric_id: id,
        date_from: yearStart,
        date_to: today,
        sort_by: 'value_date',
        sort_dir: 'asc',
        page: 1,
        page_size: 10000 // Large page size to get all values
      });
      
      setYtdValues(valuesResponse.data.items || []);
    } catch (err) {
      console.error('Error fetching YTD data:', err);
      setYtdValues([]);
    } finally {
      setYtdLoading(false);
    }
  }, [id, chartViewType]);

  // Fetch aggregated data for monthly/quarterly views
  const fetchAggregatedData = useCallback(async () => {
    if (!id || (chartViewType !== 'monthly' && chartViewType !== 'quarterly')) {
      setAggregatedValues([]);
      return;
    }
    
    try {
      setAggregatedLoading(true);
      const currentYear = new Date().getFullYear();
      const yearStart = `${currentYear}-01-01`;
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch all values for current year
      const valuesResponse = await apiService.metricValues.getAll({
        metric_id: id,
        date_from: yearStart,
        date_to: today,
        sort_by: 'value_date',
        sort_dir: 'asc',
        page: 1,
        page_size: 10000 // Large page size to get all values
      });
      
      setAggregatedValues(valuesResponse.data.items || []);
    } catch (err) {
      console.error('Error fetching aggregated data:', err);
      setAggregatedValues([]);
    } finally {
      setAggregatedLoading(false);
    }
  }, [id, chartViewType]);

  useEffect(() => {
    fetchYtdData();
    fetchAggregatedData();
  }, [fetchYtdData, fetchAggregatedData]);

  // Initialize formData with the current metric ID
  useEffect(() => {
    if (id) {
      setFormData(prev => ({
        ...prev,
        metrics_id: id
      }));
    }
  }, [id]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const calculateDefaultValueDate = async () => {
    if (!id || !metric) {
      return new Date().toISOString().split('T')[0]; // Default to today if no metric
    }

    try {
      // Fetch the latest metric value for this metric
      const latestValueResponse = await apiService.metricValues.getAll({
        metric_id: id,
        sort_by: 'value_date',
        sort_dir: 'desc',
        page: 1,
        page_size: 1
      });

      const latestValue = latestValueResponse?.data?.items?.[0];
      
      if (!latestValue || !latestValue.value_date) {
        return new Date().toISOString().split('T')[0]; // Default to today if no values exist
      }

      // Get frequency from the metric
      const frequency = metric?.frequency;

      if (!frequency || frequency <= 0) {
        // If no frequency set, default to today
        return new Date().toISOString().split('T')[0];
      }

      // Calculate next date: latest value date + frequency days
      const latestDate = new Date(latestValue.value_date);
      const nextDate = new Date(latestDate);
      nextDate.setDate(nextDate.getDate() + frequency);
      
      return nextDate.toISOString().split('T')[0];
    } catch (err) {
      console.error('Error calculating default value date:', err);
      return new Date().toISOString().split('T')[0]; // Default to today on error
    }
  };

  const resetFormData = async () => {
    const defaultDate = await calculateDefaultValueDate();
    setFormData({
      metrics_id: id, // Set the current metric ID
      value_date: defaultDate,
      value: '',
      target: '',
      comment: '',
      created_by: 1
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted with data:', formData);
    console.log('Metric ID:', id);
    
    // Basic validation - value should be provided
    if (!formData.value) {
      console.log('Validation failed: No value provided');
      setError('Please provide a numeric value');
      return;
    }
    
    console.log('Validation passed, proceeding with API call...');
    
    try {
      const dataToSubmit = {
        metrics_id: parseInt(formData.metrics_id),
        value_date: formData.value_date,
        value: formData.value ? parseFloat(formData.value) : null,
        target: formData.target ? parseFloat(formData.target) : null,
        comment: formData.comment || null,
        updated_by: 1,  // System admin
        created_by: 1  // System admin
      };

      console.log('Data to submit:', dataToSubmit);
      console.log('About to call API...');
      
      // Add timeout to detect hanging requests
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('API call timeout after 10 seconds')), 10000)
      );
      
      console.log('About to call apiService.metricValues.create with data:', dataToSubmit);
      const apiPromise = apiService.metricValues.create(dataToSubmit);
      
      const response = await Promise.race([apiPromise, timeoutPromise]);
      console.log('API call completed. Response:', response);
      console.log('Successfully created metric value');
      
      setShowAddDialog(false);
      resetFormData();
      fetchMetricDetails(); // Refresh the data
    } catch (err) {
      console.error('Error saving metric value:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        config: err.config
      });
      setError('Failed to save metric value: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCloseDialog = () => {
    setShowAddDialog(false);
    resetFormData();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleAddMetricValue = async () => {
    setShowDropdown(false);
    // Calculate and set default value date before opening dialog
    const defaultDate = await calculateDefaultValueDate();
    setFormData(prev => ({
      ...prev,
      value_date: defaultDate
    }));
    setShowAddDialog(true);
  };

  const handleEditMetric = () => {
    setShowDropdown(false);
    navigate(`/metrics?edit=${id}`);
  };

  const totalPages = Math.ceil(pagination.total / pagination.page_size);

  // Aggregate values based on aggregation type
  const aggregateValues = (values, aggregationType) => {
    if (!values || values.length === 0) return null;
    const numericValues = values.filter(v => v !== null && v !== undefined && !isNaN(v)).map(v => parseFloat(v));
    if (numericValues.length === 0) return null;
    
    switch (aggregationType?.toLowerCase()) {
      case 'sum':
        return numericValues.reduce((sum, val) => sum + val, 0);
      case 'average':
      case 'avg':
        return numericValues.reduce((sum, val) => sum + val, 0) / numericValues.length;
      case 'count':
        return numericValues.length;
      case 'min':
        return Math.min(...numericValues);
      case 'max':
        return Math.max(...numericValues);
      case 'cumulative':
        // For cumulative metrics, take the last (maximum) value in the period
        // since cumulative values only increase over time
        return Math.max(...numericValues);
      default:
        // Default to sum if aggregation type not specified
        return numericValues.reduce((sum, val) => sum + val, 0);
    }
  };

  // Get last day of month
  const getLastDayOfMonth = (year, month) => {
    return new Date(year, month + 1, 0);
  };

  // Get last day of quarter
  const getLastDayOfQuarter = (year, quarter) => {
    const month = (quarter - 1) * 3 + 2; // Last month of quarter (0-indexed: 2, 5, 8, 11)
    return new Date(year, month + 1, 0);
  };

  // Calculate monthly averages for YTD
  const calculateYtdMonthlyAverages = (values) => {
    const monthlyData = {};
    const isCumulative = metric?.aggregation_type?.toLowerCase() === 'cumulative';
    
    values.forEach(value => {
      const date = new Date(value.value_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthName,
          values: [],
          targets: [],
          valueObjects: [], // Store full objects for cumulative metrics
          targetObjects: []
        };
      }
      
      if (value.value !== null && value.value !== undefined) {
        monthlyData[monthKey].values.push(parseFloat(value.value));
        if (isCumulative) {
          monthlyData[monthKey].valueObjects.push({
            value: parseFloat(value.value),
            date: date
          });
        }
      }
      
      if (value.target !== null && value.target !== undefined) {
        monthlyData[monthKey].targets.push(parseFloat(value.target));
        if (isCumulative) {
          monthlyData[monthKey].targetObjects.push({
            value: parseFloat(value.target),
            date: date
          });
        }
      }
    });
    
    // Calculate aggregated values for each month based on aggregation type
    const chartData = Object.keys(monthlyData)
      .sort()
      .map(monthKey => {
        const month = monthlyData[monthKey];
        
        let actual, target;
        if (isCumulative) {
          // For cumulative, take the last value by date in the period
          const sortedValues = month.valueObjects.sort((a, b) => a.date - b.date);
          const sortedTargets = month.targetObjects.sort((a, b) => a.date - b.date);
          actual = sortedValues.length > 0 ? sortedValues[sortedValues.length - 1].value : null;
          target = sortedTargets.length > 0 ? sortedTargets[sortedTargets.length - 1].value : null;
        } else {
          actual = aggregateValues(month.values, metric?.aggregation_type);
          target = aggregateValues(month.targets, metric?.aggregation_type);
        }
        
        return {
          date: month.month,
          dateLabel: month.month,
          actual: actual,
          target: target
        };
      });
    
    return chartData;
  };

  // Prepare monthly aggregated chart data
  const getMonthlyChartData = (values) => {
    const currentYear = new Date().getFullYear();
    const monthlyData = {};
    const isCumulative = metric?.aggregation_type?.toLowerCase() === 'cumulative';
    
    // Filter to current year and group by month
    values.forEach(value => {
      const date = new Date(value.value_date);
      if (date.getFullYear() !== currentYear) return;
      
      const month = date.getMonth(); // 0-11
      const monthKey = `${currentYear}-${String(month + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          year: currentYear,
          month: month,
          values: [],
          targets: [],
          valueObjects: [], // Store full objects for cumulative metrics
          targetObjects: []
        };
      }
      
      if (value.value !== null && value.value !== undefined) {
        monthlyData[monthKey].values.push(parseFloat(value.value));
        if (isCumulative) {
          monthlyData[monthKey].valueObjects.push({
            value: parseFloat(value.value),
            date: date
          });
        }
      }
      
      if (value.target !== null && value.target !== undefined) {
        monthlyData[monthKey].targets.push(parseFloat(value.target));
        if (isCumulative) {
          monthlyData[monthKey].targetObjects.push({
            value: parseFloat(value.target),
            date: date
          });
        }
      }
    });
    
    // Convert to chart data
    const chartData = Object.keys(monthlyData)
      .sort()
      .map(monthKey => {
        const month = monthlyData[monthKey];
        const lastDay = getLastDayOfMonth(month.year, month.month);
        const periodLabel = lastDay.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        
        let actual, target;
        if (isCumulative) {
          // For cumulative, take the last value by date in the period
          const sortedValues = month.valueObjects.sort((a, b) => a.date - b.date);
          const sortedTargets = month.targetObjects.sort((a, b) => a.date - b.date);
          actual = sortedValues.length > 0 ? sortedValues[sortedValues.length - 1].value : null;
          target = sortedTargets.length > 0 ? sortedTargets[sortedTargets.length - 1].value : null;
        } else {
          actual = aggregateValues(month.values, metric?.aggregation_type);
          target = aggregateValues(month.targets, metric?.aggregation_type);
        }
        
        return {
          date: lastDay.toLocaleDateString(),
          dateLabel: periodLabel,
          actual: actual,
          target: target
        };
      });
    
    return chartData;
  };

  // Prepare quarterly aggregated chart data
  const getQuarterlyChartData = (values) => {
    const currentYear = new Date().getFullYear();
    const quarterlyData = {};
    const isCumulative = metric?.aggregation_type?.toLowerCase() === 'cumulative';
    
    // Filter to current year and group by quarter
    values.forEach(value => {
      const date = new Date(value.value_date);
      if (date.getFullYear() !== currentYear) return;
      
      const quarter = Math.floor(date.getMonth() / 3) + 1; // 1-4
      const quarterKey = `${currentYear}-Q${quarter}`;
      
      if (!quarterlyData[quarterKey]) {
        quarterlyData[quarterKey] = {
          year: currentYear,
          quarter: quarter,
          values: [],
          targets: [],
          valueObjects: [], // Store full objects for cumulative metrics
          targetObjects: []
        };
      }
      
      if (value.value !== null && value.value !== undefined) {
        quarterlyData[quarterKey].values.push(parseFloat(value.value));
        if (isCumulative) {
          quarterlyData[quarterKey].valueObjects.push({
            value: parseFloat(value.value),
            date: date
          });
        }
      }
      
      if (value.target !== null && value.target !== undefined) {
        quarterlyData[quarterKey].targets.push(parseFloat(value.target));
        if (isCumulative) {
          quarterlyData[quarterKey].targetObjects.push({
            value: parseFloat(value.target),
            date: date
          });
        }
      }
    });
    
    // Convert to chart data
    const chartData = Object.keys(quarterlyData)
      .sort()
      .map(quarterKey => {
        const quarter = quarterlyData[quarterKey];
        const lastDay = getLastDayOfQuarter(quarter.year, quarter.quarter);
        const periodLabel = `Q${quarter.quarter} ${quarter.year}`;
        
        let actual, target;
        if (isCumulative) {
          // For cumulative, take the last value by date in the period
          const sortedValues = quarter.valueObjects.sort((a, b) => a.date - b.date);
          const sortedTargets = quarter.targetObjects.sort((a, b) => a.date - b.date);
          actual = sortedValues.length > 0 ? sortedValues[sortedValues.length - 1].value : null;
          target = sortedTargets.length > 0 ? sortedTargets[sortedTargets.length - 1].value : null;
        } else {
          actual = aggregateValues(quarter.values, metric?.aggregation_type);
          target = aggregateValues(quarter.targets, metric?.aggregation_type);
        }
        
        return {
          date: lastDay.toLocaleDateString(),
          dateLabel: periodLabel,
          actual: actual,
          target: target
        };
      });
    
    return chartData;
  };

  // Prepare chart data based on view type
  const chartData = (() => {
    if (chartViewType === 'ytd') {
      return calculateYtdMonthlyAverages(ytdValues);
    } else if (chartViewType === 'monthly') {
      return getMonthlyChartData(aggregatedValues);
    } else if (chartViewType === 'quarterly') {
      return getQuarterlyChartData(aggregatedValues);
    } else {
      // Last 10 values
      return metricValues.slice(0, 10).map(value => ({
        date: new Date(value.value_date).toLocaleDateString(),
        dateLabel: new Date(value.value_date).toLocaleDateString(),
        actual: parseFloat(value.value),
        target: value.target ? parseFloat(value.target) : null
      })).reverse(); // Reverse to show oldest to newest
    }
  })();

  // Helper function to determine if a value is on the wrong side of the target
  const isValueOnWrongSide = (actual, target, higherIsBetter) => {
    if (actual === null || actual === undefined || target === null || target === undefined) {
      return false;
    }
    if (higherIsBetter) {
      // Higher is better: wrong side if actual < target
      return actual < target;
    } else {
      // Lower is better: wrong side if actual > target
      return actual > target;
    }
  };

  // Calculate smart Y-axis domain to handle negative values
  const getYAxisDomain = (chartData) => {
    if (!chartData || chartData.length === 0) {
      return ['auto', 'auto'];
    }

    // Extract all numeric values (actual and target)
    const allValues = chartData
      .flatMap(d => [d.actual, d.target])
      .filter(v => v !== null && v !== undefined && !isNaN(v));

    if (allValues.length === 0) {
      return ['auto', 'auto'];
    }

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const range = max - min;

    // If all values are negative, include 0 at the top
    if (max < 0) {
      return [min * 1.1, 0]; // Add 10% padding below minimum
    }

    // If values span negative and positive, include 0 in the middle
    if (min < 0 && max > 0) {
      const absRange = Math.max(Math.abs(min), Math.abs(max));
      return [min - absRange * 0.1, max + absRange * 0.1]; // Add 10% padding on both sides
    }

    // For all positive values, add padding below minimum to ensure visibility
    // Use 5% of range or a minimum of 2 units, whichever is larger
    const padding = Math.max(range * 0.05, 2);
    const minDomain = Math.max(0, min - padding); // Don't go below 0 for positive values
    
    return [minDomain, max + range * 0.05]; // Add 5% padding on both sides
  };

  if (loading) {
    return <div className="loading">Loading metric details...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!metric) {
    return <div className="error">Metric not found</div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/metrics" className="btn btn-secondary btn-small">
            ← Back to Metrics
          </Link>
          <h1 className="page-title">
            {metric.name}
            {isAutomatedMetric(metric) && <AutomatedBadge />}
          </h1>
          <p className="text-muted">Metric Details</p>
        </div>
        <div style={{display: 'flex', gap: '1rem', position: 'relative'}} ref={dropdownRef}>
          <button 
            className="btn btn-secondary" 
            onClick={() => setShowDropdown(!showDropdown)}
            style={{
              padding: '0.5rem',
              minWidth: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            aria-label="Actions menu"
          >
            <span style={{ fontSize: '1.2rem' }}>⋯</span>
          </button>
          {showDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '0.5rem',
              backgroundColor: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              zIndex: 1000,
              minWidth: '200px',
              overflow: 'hidden'
            }}>
              <button
                onClick={handleAddMetricValue}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  textAlign: 'left',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  color: '#034955',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Add Metric Value
              </button>
              <button
                onClick={handleEditMetric}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  textAlign: 'left',
                  border: 'none',
                  borderTop: '1px solid #e0e0e0',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  color: '#034955',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Edit Metric
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Metric Information - Horizontal Layout */}
      <div className="details-card" style={{ marginBottom: '2rem' }}>
        <h3>Metric Information</h3>
        <div style={{ 
          display: 'flex', 
          gap: '2rem', 
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <strong>Unit:</strong>
            <span>{metric.unit || 'No unit specified'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <strong>Data Type:</strong>
            <span>{metric.data_type || 'Not specified'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <strong>Aggregation:</strong>
            <span>{metric.aggregation_type || 'Not specified'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <strong>Frequency:</strong>
            <span>{metric.frequency ? `${metric.frequency} days` : 'Not specified'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <strong>Source:</strong>
            <span>{metric.source || 'Not specified'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <strong>Status:</strong>
            <span style={{ color: metric.is_active ? 'green' : 'red', fontWeight: 'bold' }}>
              {metric.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        {metric.description && (
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e0e0e0' }}>
            <strong>Description:</strong>
            <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>{metric.description}</p>
          </div>
        )}
      </div>

      {/* Performance Chart */}
      <div className="details-card" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>
            Performance vs Target {
              chartViewType === 'ytd' ? '(YTD - Year-To-Date)' :
              chartViewType === 'monthly' ? '(Monthly)' :
              chartViewType === 'quarterly' ? '(Quarterly)' :
              '(Last 10 Values)'
            }
          </h3>
          <select
            value={chartViewType}
            onChange={(e) => setChartViewType(e.target.value)}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '1rem',
              borderRadius: '4px',
              border: '1px solid #ccc',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="last10">Last 10 Values</option>
            <option value="ytd">YTD (Year-To-Date)</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
          </select>
        </div>
        {((ytdLoading && chartViewType === 'ytd') || (aggregatedLoading && (chartViewType === 'monthly' || chartViewType === 'quarterly'))) ? (
          <p className="text-muted">Loading data...</p>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={chartViewType === 'last10' || chartViewType === 'ytd' ? 'date' : 'dateLabel'} />
              <YAxis 
                domain={getYAxisDomain(chartData)}
                tickFormatter={(value) => formatNumberForChart(value, metric?.display_format || 'number', metric?.unit)}
              />
              <Tooltip 
                formatter={(value, name) => {
                  // In Recharts, the 'name' parameter is the 'name' prop from the component
                  // Bar has name="Actual", Line has name="Target"
                  const label = name === 'Actual' ? 'Actual' : 'Target';
                  const formattedValue = formatNumberForChart(
                    value, 
                    metric?.display_format || 'number',
                    metric?.unit
                  );
                  return [formattedValue, label];
                }}
              />
              <Legend />
              <Bar dataKey="actual" name="Actual">
                {chartData.map((entry, index) => {
                  const isWrongSide = isValueOnWrongSide(
                    entry.actual,
                    entry.target,
                    metric?.higher_is_better !== false // Default to true if not set
                  );
                  return (
                    <Cell key={`cell-${index}`} fill={isWrongSide ? '#DC3545' : '#034955'} />
                  );
                })}
              </Bar>
              <Line 
                type="monotone" 
                dataKey="target" 
                stroke="#DE37A4" 
                strokeWidth={3}
                dot={{ fill: '#DE37A4', strokeWidth: 2, r: 4 }}
                name="Target" 
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-muted">No data available for chart</p>
        )}
      </div>

      {/* Metric Values */}
      <div className="details-card">
        <h3>Metric Values ({pagination.total})</h3>
        {metricValues.length > 0 ? (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Value</th>
                    <th>Date</th>
                    <th>Target</th>
                    <th>Source</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {metricValues.map(value => (
                    <tr key={value.id}>
                      <td>
                        <span className="value-number">
                          {formatNumber(value.value, metric?.display_format || 'number', metric?.unit)}
                        </span>
                      </td>
                      <td>{new Date(value.value_date).toLocaleDateString()}</td>
                      <td>
                        {value.target ? (
                          <span className="value-number">
                            {formatNumber(value.target, metric?.display_format || 'number', metric?.unit)}
                          </span>
                        ) : (
                          <span className="text-muted">No target</span>
                        )}
                      </td>
                      <td>
                        {value.is_automated ? <AutoValueBadge /> : <span className="text-muted">Manual</span>}
                      </td>
                      <td>{value.comment || <span className="text-muted">No notes</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.page} of {totalPages}
                </span>
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-muted">No values recorded for this metric.</p>
        )}
      </div>

      {/* Add Metric Value Dialog */}
      {showAddDialog && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3>Add New Metric Value</h3>
            <form onSubmit={handleSubmit} noValidate>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Value Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.value_date}
                    onChange={(e) => {
                      console.log('Date changed to:', e.target.value);
                      setFormData(prev => ({...prev, value_date: e.target.value}));
                    }}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Metric</label>
                  <input
                    type="text"
                    className="form-control"
                    value={metric.name}
                    disabled
                    style={{backgroundColor: '#f8f9fa'}}
                  />
                </div>
              </div>

              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Numeric Value</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={formData.value}
                    onChange={(e) => {
                      console.log('Value changed to:', e.target.value);
                      setFormData(prev => {
                        const newData = {...prev, value: e.target.value};
                        console.log('New form data:', newData);
                        return newData;
                      });
                    }}
                    placeholder="Enter numeric value"
                  />
                  {metric.unit && <small className="text-muted">Unit: {metric.unit}</small>}
                </div>
                <div className="form-group">
                  <label className="form-label">Numeric Target</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={formData.target}
                    onChange={(e) => setFormData(prev => ({...prev, target: e.target.value}))}
                    placeholder="Enter target value"
                  />
                  {metric.unit && <small className="text-muted">Unit: {metric.unit}</small>}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Comment</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.comment}
                  onChange={(e) => setFormData(prev => ({...prev, comment: e.target.value}))}
                  placeholder="Add any comments or notes..."
                />
              </div>

              <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
                <button type="button" className="btn btn-secondary" onClick={handleCloseDialog}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                >
                  Add Metric Value
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricsDetails;
